import React from 'react';
import {
  Box, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, Typography, alpha, useTheme,
} from '@mui/material';
import { AccountBalanceWalletOutlined } from '@mui/icons-material';
import { CashBankResponse, JOURNAL_SOURCE_LABEL } from '../../../types/finance';
import { useTimezone } from '../../../hooks/useTimezone';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import EmptyState from '../../common/EmptyState';
import Amount from '../ledger/Amount';
import MovementDescription from '../ledger/MovementDescription';

/** Movements on cash and bank, newest first; a row opens its voucher. */
const CashMovementsTable: React.FC<{
  data: CashBankResponse | null;
  loading: boolean;
  error: string | null;
  filtered: boolean;
  page: number;
  limit: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  onOpen: (entryId: string) => void;
}> = ({ data, loading, error, filtered, page, limit, onPage, onLimit, onOpen }) => {
  const theme = useTheme();
  const { formatDate } = useTimezone();
  const items = data?.items ?? [];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
      {loading && items.length > 0 && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
      {data && (
        <Stack direction="row" spacing={3} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
          {([['In', data.totals.inPaisa, 'success.main'], ['Out', data.totals.outPaisa, 'error.main'], ['Net', data.totals.netPaisa, 'text.primary']] as const).map(([label, paisa, color]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary">{label}{filtered ? ' (filtered)' : ''}</Typography>
              <Box><Amount paisa={paisa} blankZero={false} fontWeight={700} color={color} /></Box>
            </Box>
          ))}
        </Stack>
      )}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell sx={{ width: 110 }}>Date</TableCell>
              <TableCell sx={{ width: 160 }}>Voucher</TableCell>
              <TableCell>Description</TableCell>
              <TableCell sx={{ width: 120 }}>Source</TableCell>
              <TableCell sx={{ width: 170 }}>Account</TableCell>
              <TableCell sx={{ width: 110 }} align="right">In</TableCell>
              <TableCell sx={{ width: 110 }} align="right">Out</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && items.length === 0 ? <TableRowSkeletons columns={7} /> : items.map(m => (
              <TableRow key={m.lineId} hover onClick={() => onOpen(m.entryId)} sx={{ cursor: 'pointer' }}>
                <TableCell><Typography variant="body2" noWrap>{formatDate(m.entryDate)}</Typography></TableCell>
                <TableCell><Typography variant="body2" fontWeight={700} noWrap sx={{ fontFamily: 'monospace' }}>{m.entryNo}</Typography></TableCell>
                <TableCell>
                  <MovementDescription text={m.memo} status={m.status} isReversal={m.isReversal} entryId={m.entryId} createdByName={m.createdByName} />
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{JOURNAL_SOURCE_LABEL[m.sourceType] || m.sourceType}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 170 }} title={`${m.account.code} · ${m.account.name}`}>
                    <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 0.75 }}>{m.account.code}</Box>{m.account.name}
                  </Typography>
                </TableCell>
                <TableCell align="right"><Amount paisa={m.inPaisa} color={m.inPaisa ? 'success.main' : undefined} fontWeight={600} /></TableCell>
                <TableCell align="right"><Amount paisa={m.outPaisa} color={m.outPaisa ? 'error.main' : undefined} fontWeight={600} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && items.length === 0 && (
        <Box sx={{ p: 3 }}>
          <EmptyState
            icon={<AccountBalanceWalletOutlined />}
            title={error ? 'Cash & bank could not load' : filtered ? 'No movements match' : 'No money has moved yet'}
            description={error || (filtered ? 'Clear a filter to see more.' : 'Fee receipts, expenses, income and fines appear here as they are recorded.')}
            dense
          />
        </Box>
      )}

      <TablePagination
        component="div"
        count={data?.pagination.totalItems ?? 0}
        page={page}
        onPageChange={(_, p) => onPage(p)}
        rowsPerPage={limit}
        onRowsPerPageChange={e => onLimit(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
};

export default CashMovementsTable;
